import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import wallImage from '../../images/wall.webp';
import wallImageMobile from '../../images/wall-mobile.webp';
import './MemorialWall.css';
import Note from '../Note';
import MemorialModal from '../MemorialModal';
import useDeviceDetection from '../../hooks/useDeviceDetection';
import useTimeOfDay from '../../hooks/useTimeOfDay';
import memorialService from '../../services/memorialService';
import { getLitCandles } from '../CandleButton';
import { getPlacedStones } from '../StoneButton';
import RememberButton from '../RememberButton';

/**
 * Main component for displaying the memorial wall with interactive notes
 * @returns {JSX.Element} Rendered MemorialWall component
 */
const MemorialWall = () => {
  // Browser detection states - moved to the top to avoid initialization issues
  const [isSafari, setIsSafari] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  
  // Main component states
  const [selectedNote, setSelectedNote] = useState(null);
  const [showIntro, setShowIntro] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  // Get sections from centralized config (single source of truth) - defined early for use throughout
  const sections = memorialService.getSections();
  // Create a custom hook for localStorage to avoid unnecessary re-renders
  const useLocalStorage = (key, initialValue) => {
    // Create state with initial value from localStorage or fallback
    const [storedValue, setStoredValue] = useState(() => {
      try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : initialValue;
      } catch (error) {
        console.error(`Error accessing localStorage key "${key}":`, error);
        return initialValue;
      }
    });
    
    // Create a memoized setter function
    const setValue = useCallback((value) => {
      try {
        // Allow value to be a function as with regular useState
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        
        // Update state
        setStoredValue(valueToStore);
        
        // Update localStorage with debounce for better performance
        // Only update localStorage when component is idle to avoid jank
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => {
            localStorage.setItem(key, JSON.stringify(valueToStore));
          }, { timeout: 500 });
        } else {
          // Fallback to setTimeout for browsers that don't support requestIdleCallback
          setTimeout(() => {
            localStorage.setItem(key, JSON.stringify(valueToStore));
          }, 100);
        }
      } catch (error) {
        console.error(`Error setting localStorage key "${key}":`, error);
      }
    }, [key, storedValue]);
    
    return [storedValue, setValue];
  };
  
  // Use optimized localStorage hook
  const [viewedNotes, setViewedNotes] = useLocalStorage('viewedMemorialNotes', {});

  // Track lit candles - initialized from localStorage
  const [litCandles, setLitCandles] = useState(() => getLitCandles());

  // Track placed stones - initialized from localStorage
  const [placedStones, setPlacedStones] = useState(() => getPlacedStones());

  // Callback for when a candle is lit in the modal
  const handleCandleLit = useCallback((memorialId) => {
    setLitCandles(getLitCandles());
  }, []);

  // Callback for when a stone is placed in the modal
  const handleStonePlaced = useCallback((memorialId) => {
    setPlacedStones(getPlacedStones());
  }, []);
  
  // No longer need to track scroll position with Intersection Observer
  const isMobile = useDeviceDetection();

  // Time-aware ambient lighting
  const { period: timePeriod } = useTimeOfDay();
  const containerRef = useRef(null);
  const scrollableContentRef = useRef(null);
  const wallRef = useRef(null);
  const touchStartY = useRef(null);  // Store touch start position for gestures
  const searchInputRef = useRef(null); // Reference to the search input
  
  // Ensure first section is always active initially
  const [activeSection, setActiveSection] = useState('section-1');
  
  // Remove state-based background position tracking to avoid React re-renders
  // We'll use direct DOM manipulation instead for the background position
  const backgroundRef = useRef(null);

  // Track active timeouts and animation frames for proper cleanup - MOVED UP
  const activeTimeoutsRef = useRef([]);
  const activeRAFsRef = useRef([]);
  
  // Helper function to create timeout with tracking - MOVED UP
  const createTimeout = useCallback((callback, delay) => {
    const id = setTimeout(() => {
      // Remove from active timeouts when executed
      activeTimeoutsRef.current = activeTimeoutsRef.current.filter(t => t !== id);
      callback();
    }, delay);
    
    // Add to active timeouts
    activeTimeoutsRef.current.push(id);
    return id;
  }, []);

  // Helper function to create RAF with tracking - MOVED UP
  const createRAF = useCallback((callback) => {
    const id = requestAnimationFrame((timestamp) => {
      // Remove from active RAFs when executed
      activeRAFsRef.current = activeRAFsRef.current.filter(r => r !== id);
      callback(timestamp);
    });
    
    // Add to active RAFs
    activeRAFsRef.current.push(id);
    return id;
  }, []);
  
  // Cache section elements refs to avoid repeated DOM lookups
  // Initialize dynamically from section config
  const initialSectionRefs = {};
  memorialService.getSections().forEach(section => {
    initialSectionRefs[section.id] = null;
  });
  const sectionRefs = useRef(initialSectionRefs);
  
  // Store previous scroll state to avoid unnecessary updates - define before using
  const prevScrollState = useRef({
    activeSection: 'section-1',
    backgroundPosition: 0,
    lastScrollPosition: 0
  });
  
  // Initialize section refs once on mount and force initial background position
  useEffect(() => {
    // Initialize section refs dynamically
    const newRefs = {};
    sections.forEach(section => {
      newRefs[section.id] = document.getElementById(`${section.id}-content`);
    });
    sectionRefs.current = newRefs;
    
    // Force initial background position update (fixes parallax initialization)
    if (scrollableContentRef.current) {
      const scrollElement = scrollableContentRef.current;
      const scrollPosition = scrollElement.scrollTop;
      const viewportHeight = scrollElement.clientHeight;
      const scrollHeight = scrollElement.scrollHeight;
      
      // Calculate initial scroll progress
      const scrollProgress = scrollPosition / (scrollHeight - viewportHeight);
      
      // Calculate initial scroll position for background
      const initialParallaxAmount = scrollProgress * (isMobile ? 20 : 30);
      
      // Store initial position in ref for comparison
      prevScrollState.current.lastScrollPosition = scrollPosition;
      
      // Apply initial position to the DOM directly when background element is available
      const bgElement = document.getElementById('wall-background');
      if (bgElement) {
        // For all browsers, use background-position for initial setup
        bgElement.style.backgroundPosition = `center ${initialParallaxAmount}%`;
        
        // Add Safari-specific optimizations
        if (isSafari || isIOS) {
          // Force GPU rendering in Safari with minimal transform
          bgElement.style.transform = 'translateZ(0.1px)';
          bgElement.style.willChange = 'background-position';
          
          // Add Safari-specific class for any CSS-based fixes
          bgElement.classList.add('safari-background');
        }
        
        // Store scroll position for future reference
        bgElement.dataset.lastScrollTop = scrollPosition;
      }
    }
  }, [isMobile, isSafari, isIOS, sections]);
  
  // Effect to set up the background element reference
  useEffect(() => {
    // Get reference to the background element after render
    backgroundRef.current = document.getElementById('wall-background');
    
    // Preload image for performance
    if (backgroundRef.current) {
      const preload = new Image();
      preload.src = isMobile ? wallImageMobile : wallImage;
    }
  }, [isMobile, wallImage, wallImageMobile]);
  
  // Completely new scroll handler that uses direct DOM manipulation
  // This avoids React state updates which can cause stuttering
  const handleScroll = useCallback(() => {
    if (!scrollableContentRef.current || !backgroundRef.current) return;
    
    // Get scroll metrics with minimal computation
    const scrollElement = scrollableContentRef.current;
    const bgElement = backgroundRef.current;
    const scrollPosition = scrollElement.scrollTop;
    
    // Use a variable stored in the DOM element itself rather than in React state
    // This avoids React re-renders entirely
    const lastPos = bgElement.dataset.lastScrollTop ? 
                    parseFloat(bgElement.dataset.lastScrollTop) : 0;
    
    // Check for significant movement before processing (saves CPU)
    // Higher threshold on mobile to reduce jitter
    const threshold = isMobile ? 8 : 2;
    if (Math.abs(scrollPosition - lastPos) < threshold) return;
    
    // Save current position for next comparison
    bgElement.dataset.lastScrollTop = scrollPosition;
    
    // Get scrollable dimensions
    const viewportHeight = scrollElement.clientHeight;
    const scrollHeight = scrollElement.scrollHeight;
    
    // Calculate scroll progress (0 to 1) - simplified formula
    const scrollRatio = scrollPosition / Math.max(1, scrollHeight - viewportHeight);
    
    // Calculate parallax position with pure DOM manipulation
    // Multiply by a factor that looks good visually
    const parallaxAmount = scrollRatio * (isMobile ? 20 : 30); 
    
    // Apply directly to the DOM element for maximum smoothness
    if (isSafari || isIOS) {
      // For Safari: use both background-position and a tiny transform for hardware acceleration
      // The transform trigger is important to get Safari's GPU acceleration
      const yPercent = parallaxAmount;

      // Safari needs background-position after all
      bgElement.style.backgroundPosition = `center ${yPercent}%`;

      // Add a negligible transform to force GPU rendering (hardware acceleration)
      bgElement.style.transform = 'translateZ(0.1px)';

      // Add additional Safari-specific properties to ensure smooth scrolling
      bgElement.style.willChange = 'background-position';

      // Log the values for debugging (uncomment if needed)
      // console.log('Safari parallax:', yPercent);
    } else {
      // Chrome/Firefox perform better with background-position
      bgElement.style.backgroundPosition = `center ${parallaxAmount}%`;
    }

    // Update parallax depth layers with different scroll rates (skip on mobile - layers hidden via CSS)
    if (!isMobile) {
      const parallaxLayers = document.querySelectorAll('.parallax-layer');
      parallaxLayers.forEach(layer => {
        const rate = parseFloat(layer.dataset.parallaxRate) || 0.5;
        const layerOffset = scrollPosition * rate * 0.15; // Scale down for subtlety
        layer.style.transform = `translateY(${-layerOffset}px)`;
      });
    }
    
    // Section handling - check if all section refs are available
    const sectionElements = sectionRefs.current;
    const allSectionsLoaded = Object.values(sectionElements).every(el => el !== null);
    if (!allSectionsLoaded) return;
    
    // Determine active section - use a less frequent check to avoid layout thrashing
    // Only check active section every 15px of scroll or after 250ms since last check
    const scrollDelta = Math.abs(scrollPosition - (prevScrollState.current.lastScrollPosition || 0));
    const timeSinceLastSectionCheck = Date.now() - (prevScrollState.current.lastSectionCheck || 0);
    if (scrollDelta > 15 || timeSinceLastSectionCheck > 250) {
      // Calculate which section is most visible
      const viewportCenter = scrollPosition + (viewportHeight / 2);
      
      // Calculate section center positions
      const sectionCenters = Object.entries(sectionElements).map(([id, el]) => ({
        id,
        distance: Math.abs(viewportCenter - (el.offsetTop + el.clientHeight / 2))
      }));
      
      // Find closest section by center
      const closestSection = sectionCenters.reduce((closest, current) => 
        current.distance < closest.distance ? current : closest
      );
      
      // Only update state if section changed (reduces renders)
      if (closestSection.id !== prevScrollState.current.activeSection) {
        setActiveSection(closestSection.id);
        prevScrollState.current.activeSection = closestSection.id;
      }
      
      // Update section check timestamp
      prevScrollState.current.lastSectionCheck = Date.now();
    }
  }, [isMobile, isIOS, isSafari, setActiveSection]);
  
  // Super-simplified requestAnimationFrame handler that focuses just on smoothness
  const useSimpleRAF = (callback) => {
    const pendingRef = useRef(false);
    const callbackRef = useRef(callback);
    
    // Update callback ref when callback changes
    useEffect(() => {
      callbackRef.current = callback;
    }, [callback]);
    
    // Return a simple throttled function
    return useCallback(() => {
      // If we already have a frame pending, do nothing
      if (pendingRef.current) return;
      
      // Set pending flag and request frame
      pendingRef.current = true;
      
      // Schedule for next frame
      requestAnimationFrame(() => {
        // Execute callback and clear pending flag
        callbackRef.current();
        pendingRef.current = false;
      });
    }, []);
  };
  
  // Create a simple, efficient scroll handler
  const optimizedScrollHandler = useSimpleRAF(handleScroll);
  
  // Set up scroll event listener with forced initial handling
  useEffect(() => {
    const scrollElement = scrollableContentRef.current;
    if (scrollElement) {
      // Use optimized scrolling for all browsers
      // Use passive event for better performance
      scrollElement.addEventListener('scroll', optimizedScrollHandler, { passive: true });
      
      // Force immediate handling for initial state
      // Use requestAnimationFrame to ensure DOM is ready
      const initialRaf = requestAnimationFrame(() => {
        // Force initial scroll handler execution
        handleScroll();
        
        // Force a tiny scroll to trigger parallax effect if user hasn't scrolled
        if (scrollElement.scrollTop === 0) {
          scrollElement.scrollTop = 0.1;
          // Then back to avoid visible jump
          setTimeout(() => {
            scrollElement.scrollTop = 0;
          }, 20);
        }
      });
      
      return () => {
        scrollElement.removeEventListener('scroll', optimizedScrollHandler);
        cancelAnimationFrame(initialRaf);
      };
    }
  }, [optimizedScrollHandler, handleScroll, isSafari, isIOS]);
  
  // Optimized IntersectionObserver with better cleanup and performance
  useEffect(() => {
    // Create observer with options
    const observerOptions = { 
      threshold: 0.1,
      // Add root margin to start animation slightly before elements come into view
      rootMargin: '50px' 
    };
    
    // Create a single observer instance for all notes
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('note-appear');
            // Unobserve after animation is triggered to improve performance
            observer.unobserve(entry.target);
          }
        });
      },
      observerOptions
    );
    
    // Use a small timeout to ensure DOM has updated
    const observerTimeout = setTimeout(() => {
      const notes = document.querySelectorAll('.note');
      notes.forEach((note) => {
        // Only observe notes that don't already have the animation class
        if (!note.classList.contains('note-appear')) {
          observer.observe(note);
        }
      });
    }, 100);

    // Clean up the observer on unmount
    return () => {
      clearTimeout(observerTimeout);
      observer.disconnect();
    };
  }, []);
  
  // Safari detection moved to the top of the component for better initialization

  // Auto-hide intro after 5 seconds
  useEffect(() => {
    if (showIntro) {
      const timer = setTimeout(() => {
        setShowIntro(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showIntro]);

  // Function to scroll to a specific section with visual transition indicator
  const scrollToSection = useCallback((sectionId) => {
    // Update active section immediately for UI feedback
    setActiveSection(sectionId);
    
    // Get the content section (not the header)
    const contentSection = document.getElementById(`${sectionId}-content`);
    if (contentSection && scrollableContentRef.current) {
      // Calculate section position and viewport height
      const sectionTop = contentSection.offsetTop;
      const headerOffset = isMobile ? 40 : 50; // Header offset for mobile/desktop
      
      // Create transition indicator element
      const existingIndicator = document.querySelector('.section-transition-indicator');
      if (existingIndicator) {
        existingIndicator.remove(); // Remove any existing indicator
      }
      
      const transitionIndicator = document.createElement('div');
      transitionIndicator.className = 'section-transition-indicator';
      
      // Get the section title for the indicator
      const sectionTitle = sections.find(section => section.id === sectionId)?.title || '';
      transitionIndicator.textContent = `${sectionTitle}`;
      
      document.body.appendChild(transitionIndicator);
      
      // Add appearing class with our tracked timer and use RAF for smoother animation
      // Force a browser reflow before adding the class for smoother animation
      transitionIndicator.getBoundingClientRect();
      
      // Use RAF to ensure the animation starts at the next frame for optimal smoothness
      createRAF(() => {
        // Use our tracked timeout for the animation delay
        createTimeout(() => {
          transitionIndicator.classList.add('appearing');
        }, 10);
      });
      
      // Announce to screen readers
      const announcement = document.getElementById('memorial-announcement');
      if (announcement) {
        announcement.textContent = `Navigating to section: ${sectionTitle}`;
      }
      
      // Use custom scrolling for all browsers for consistent behavior
      // Smoother scrolling using RAF
      const startTime = performance.now();
      const startPosition = scrollableContentRef.current.scrollTop;
      const targetPosition = sectionTop - headerOffset;
      
      // Detect Chrome for best performance
      const isChrome = /chrome/i.test(navigator.userAgent) && !/edge|edg/i.test(navigator.userAgent);
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      // Set optimal duration based on browser and distance
      const distance = Math.abs(targetPosition - startPosition);
      // Optimize duration based on distance and browser
      const duration = isChrome 
        ? Math.min(500, 300 + distance * 0.2) // Even shorter durations for Chrome 
        : isSafari
          ? 800 // Fixed longer duration for Safari
          : 700; // Middle ground for other browsers
      
      // The scroll animation has been replaced with the enhanced version below
      // that uses the tracked createRAF function for better performance and cleanup
      
      // Use our tracked RAF for consistent performance and better cleanup
      const scrollAnimationRef = { current: null };
      
      // Enhanced scroll animation function with better tracking
      const enhancedAnimateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Enhanced easing functions for smoother, more natural feel
        let easedProgress;
        
        // Different easing based on browser testing
        if (isChrome) {
          // Chrome benefits from a more aggressive acceleration curve
          if (progress < 0.4) {
            // Fast initial acceleration for Chrome - feels snappier
            easedProgress = 4.5 * progress * progress; // Even faster acceleration
          } else if (progress < 0.65) {
            // Maintain speed in the middle
            easedProgress = 0.85 * progress + 0.1;
          } else {
            // Smoother deceleration at the end - more natural stop
            easedProgress = 1 - Math.pow(-2 * progress + 2, 2.2) / 2;
          }
        } else if (isSafari) {
          // Safari benefits from a gentler curve with longer deceleration
          if (progress < 0.3) {
            // More gentle start for Safari's rendering engine
            easedProgress = 3 * progress * progress;
          } else if (progress < 0.8) {
            // Maintain good speed in the middle portion
            easedProgress = 0.7 * progress + 0.15;
          } else {
            // Longer, smoother deceleration at the end for Safari
            easedProgress = 1 - Math.pow(1 - progress, 3);
          }
        } else {
          // Default easing for other browsers - balanced approach
          easedProgress = progress < 0.5 ?
            4 * progress * progress * progress :
            1 - Math.pow(-2 * progress + 2, 3) / 2;
        }
        
        // Calculate and set new position
        const newPosition = startPosition + (targetPosition - startPosition) * easedProgress;
        scrollableContentRef.current.scrollTop = newPosition;
        
        // Continue until done
        if (progress < 1) {
          // Use tracked RAF for better cleanup
          scrollAnimationRef.current = createRAF(enhancedAnimateScroll);
        } else {
          // Animation is complete - start cleanup
          // Track this for proper memory management
          const cleanupDuration = isChrome ? 500 : isSafari ? 800 : 700;
          
          // Use our tracked timeout for automatic cleanup
          createTimeout(() => {
            transitionIndicator.classList.add('disappearing');
            
            // Force active section update after scrolling is completed
            setActiveSection(sectionId);
            
            // Special Safari fix: force background update after section change
            if (isSafari || isIOS) {
              // Get background element
              const bgElement = document.getElementById('wall-background');
              if (bgElement) {
                // Force a background redraw for Safari by temporarily adjusting styles
                // This helps ensure Safari properly updates the background when scrolling
                const currentTransform = bgElement.style.transform;
                
                // Force a small style change to trigger a repaint
                bgElement.style.willChange = 'background-position, transform';
                bgElement.style.transform = 'translateZ(0.2px)';
                
                // Apply the proper scroll position again
                const scrollElement = scrollableContentRef.current;
                if (scrollElement) {
                  // Recalculate scroll position and force an update
                  handleScroll();
                }
                
                // Restore original transform after a tiny delay
                setTimeout(() => {
                  bgElement.style.transform = currentTransform || 'translateZ(0.1px)';
                  bgElement.style.willChange = 'background-position';
                }, 50);
              }
            }
            
            // Remove the indicator after fade out using tracked timeout
            createTimeout(() => {
              if (document.body.contains(transitionIndicator)) {
                document.body.removeChild(transitionIndicator);
              }
            }, 300);
          }, cleanupDuration);
        }
      };
      
      // Start animation with proper tracking
      scrollAnimationRef.current = createRAF(enhancedAnimateScroll);
    }
  }, [isMobile, setActiveSection, createRAF, createTimeout]);

  // Add touch gesture handling for swipe navigation
  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);
  
  const handleTouchEnd = useCallback((e) => {
    if (!touchStartY.current) return;
    
    const touchEndY = e.changedTouches[0].clientY;
    const diffY = touchStartY.current - touchEndY;
    
    // Reset the ref
    touchStartY.current = null;
    
    // Minimum swipe distance (px)
    const minSwipeDistance = 50;
    
    // Ignore small movements
    if (Math.abs(diffY) < minSwipeDistance) return;
    
    // Get current section index from dynamic section config
    const sectionIds = sections.map(s => s.id);
    const sectionIndex = sectionIds.findIndex(id => id === activeSection);
    
    if (diffY > 0) {
      // Swipe up - go to next section
      const nextSectionIndex = Math.min(sectionIndex + 1, sectionIds.length - 1);
      if (nextSectionIndex !== sectionIndex) {
        scrollToSection(sectionIds[nextSectionIndex]);
      }
    } else {
      // Swipe down - go to previous section
      const prevSectionIndex = Math.max(sectionIndex - 1, 0);
      if (prevSectionIndex !== sectionIndex) {
        scrollToSection(sectionIds[prevSectionIndex]);
      }
    }
  }, [activeSection, scrollToSection, sections]);

  // Touch swipe navigation disabled on mobile - was causing scroll jitter
  // Users can scroll naturally or tap section tabs to navigate
  // useEffect(() => {
  //   const scrollable = scrollableContentRef.current;
  //   if (scrollable && isMobile) {
  //     scrollable.addEventListener('touchstart', handleTouchStart);
  //     scrollable.addEventListener('touchend', handleTouchEnd);
  //
  //     return () => {
  //       scrollable.removeEventListener('touchstart', handleTouchStart);
  //       scrollable.removeEventListener('touchend', handleTouchEnd);
  //     };
  //   }
  // }, [handleTouchStart, handleTouchEnd, isMobile]);
  
  // Improved keyboard navigation for notes
  const handleKeyboardNavigation = useCallback((e) => {
    // Only handle tab and arrow keys for navigation
    if (!['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) || e.shiftKey) {
      return;
    }
    
    // Handle arrow key navigation between notes
    if (e.key.startsWith('Arrow')) {
      e.preventDefault(); // Prevent default scrolling behavior
      
      // Get all focusable notes in the current active section
      const activeContentSection = document.getElementById(`${activeSection}-content`);
      if (!activeContentSection) return;
      
      const sectionNotes = Array.from(activeContentSection.querySelectorAll('.note[tabindex="0"]'));
      if (sectionNotes.length === 0) return;
      
      // Find currently focused note
      const currentFocusIndex = sectionNotes.findIndex(note => note === document.activeElement);
      let nextIndex = 0;
      
      // If a note is currently focused, find the next note based on direction
      if (currentFocusIndex !== -1) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          // Move to next note
          nextIndex = (currentFocusIndex + 1) % sectionNotes.length;
        } else {
          // Move to previous note
          nextIndex = (currentFocusIndex - 1 + sectionNotes.length) % sectionNotes.length;
        }
      }
      
      // Focus the next note
      sectionNotes[nextIndex].focus();
      
      // Announce to screen readers
      const announcement = document.getElementById('memorial-announcement');
      if (announcement) {
        const noteName = sectionNotes[nextIndex].getAttribute('aria-label');
        announcement.textContent = `Navigated to ${noteName}`;
      }
    }
  }, [activeSection]);
  
  // Add keyboard navigation event listener
  useEffect(() => {
    const contentContainer = scrollableContentRef.current;
    if (contentContainer) {
      contentContainer.addEventListener('keydown', handleKeyboardNavigation);
      
      return () => {
        contentContainer.removeEventListener('keydown', handleKeyboardNavigation);
      };
    }
  }, [handleKeyboardNavigation]);

  // Optimized note click handler with memoization
  const handleNoteClick = useCallback((note) => {
    setSelectedNote(note);
    setShowIntro(false);
    
    // Track viewed notes - localStorage is handled by the hook now
    setViewedNotes(prev => ({...prev, [note.id]: true}));
    
    // Announce to screen readers
    const announcement = document.getElementById('memorial-announcement');
    if (announcement) {
      announcement.textContent = `Showing memorial information for ${note.name}`;
    }
  }, [setSelectedNote, setShowIntro, setViewedNotes]);

  const handleCloseInfo = () => {
    setSelectedNote(null);
  };

  const handleIntroClose = () => {
    setShowIntro(false);
  };

  const memorials = memorialService.getMemorials(isMobile);
  
  // Check for URL params for deep linking
  useEffect(() => {
    // Parse URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const memorialId = urlParams.get('memorial');
    
    // If memorial ID is in URL, open that memorial
    if (memorialId) {
      const id = parseInt(memorialId, 10);
      const memorialToOpen = memorials.find(m => m.id === id);
      
      if (memorialToOpen) {
        // Slight delay to ensure component is fully mounted
        setTimeout(() => {
          setSelectedNote(memorialToOpen);
          setShowIntro(false);
          
          // Also mark as viewed
          setViewedNotes(prev => {
            const newViewedNotes = {...prev, [memorialToOpen.id]: true};
            
            // Store in localStorage for persistence
            try {
              localStorage.setItem('viewedMemorialNotes', JSON.stringify(newViewedNotes));
            } catch (e) {
              console.error('Error saving viewed notes to localStorage', e);
            }
            
            return newViewedNotes;
          });
          
          // Update URL to remove parameters
          window.history.replaceState({}, document.title, window.location.pathname);
        }, 300);
      }
    }
  }, [memorials]);
  
  // Section ID mappings from centralized config (single source of truth)
  // Positions come from the memorial data, calculated by positioning.js
  const sectionData = useMemo(() => {
    const config = memorialService.getSectionConfig();
    const idMap = {};

    Object.entries(config).forEach(([sectionId, sectionConfig]) => {
      idMap[sectionId] = sectionConfig.entryIds;
    });

    return { idMap };
  }, []);
  
  // Memorials already have positions calculated by the centralized positioning system
  // No need to redistribute - positions come from the data
  const distributedMemorials = memorials;
  
  // Pre-filter memorials into sections using centralized config
  const sectionMemorials = useMemo(() => {
    const result = {};
    Object.keys(sectionData.idMap).forEach(sectionId => {
      result[sectionId] = distributedMemorials.filter(
        note => sectionData.idMap[sectionId]?.includes(note.id)
      );
    });
    return result;
  }, [distributedMemorials, sectionData]);
  
  // Simple virtualization - track visible sections to avoid rendering off-screen notes
  // Initialize dynamically from sections config
  const [visibleSections, setVisibleSections] = useState(() => {
    const initial = {};
    memorialService.getSections().forEach((section, index) => {
      initial[section.id] = index === 0; // First section visible by default
    });
    return initial;
  });
  
  // Observer for section visibility tracking
  useEffect(() => {
    // Create intersection observer for sections
    const sectionObserver = new IntersectionObserver(
      (entries) => {
        // Process intersections and update visible sections
        const newVisibleSections = { ...visibleSections };
        entries.forEach(entry => {
          // Extract section ID from element ID
          const sectionId = entry.target.id.replace('-content', '');
          // Set visibility based on intersection
          newVisibleSections[sectionId] = entry.isIntersecting;
          
          // If a section is almost in view, also mark it as visible for smoother experience
          if (!entry.isIntersecting) {
            // Check if the section is close to the viewport (within 1000px)
            const viewportHeight = window.innerHeight;
            const distanceToViewport = Math.min(
              Math.abs(entry.boundingClientRect.top),
              Math.abs(entry.boundingClientRect.bottom - viewportHeight)
            );
            
            // If section is within 1000px of the viewport, still mark it as visible
            if (distanceToViewport < 1000) {
              newVisibleSections[sectionId] = true;
            }
          }
        });
        
        // Update state only if something changed
        if (JSON.stringify(newVisibleSections) !== JSON.stringify(visibleSections)) {
          setVisibleSections(newVisibleSections);
        }
      },
      {
        // Root is the scrollable container
        root: scrollableContentRef.current,
        // Use a small threshold
        threshold: 0.1,
        // Add root margin to start loading content before it's visible
        rootMargin: '500px 0px 500px 0px' // 500px margin top and bottom
      }
    );
    
    // Observe each section
    Object.keys(sectionMemorials).forEach(sectionId => {
      const sectionElement = document.getElementById(`${sectionId}-content`);
      if (sectionElement) {
        sectionObserver.observe(sectionElement);
      }
    });
    
    // Clean up observer
    return () => {
      sectionObserver.disconnect();
    };
  }, [sectionMemorials, visibleSections]);
  
  // Filter memorials based on search term
  const filteredMemorials = useMemo(() => {
    if (!searchTerm.trim()) return distributedMemorials;
    
    return distributedMemorials.filter(note => 
      note.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [distributedMemorials, searchTerm]);
  
  // Toggle search visibility
  const toggleSearch = useCallback(() => {
    setShowSearch(prevState => {
      const newState = !prevState;
      
      // Focus search input if opening
      if (newState && searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current.focus();
        }, 100);
      } else {
        // Clear search when closing
        setSearchTerm('');
      }
      
      return newState;
    });
  }, []);

  // Tracking functions moved to top of component for proper initialization order
  
  // Enhanced cleanup for component unmount - proper tracking of all async operations
  useEffect(() => {
    // Create a method to be shared across the component for clearing memory
    window.memorialWallCleanup = () => {
      // Clear all tracked timeouts
      activeTimeoutsRef.current.forEach(id => {
        clearTimeout(id);
      });
      activeTimeoutsRef.current = [];
      
      // Clear all tracked animation frames
      activeRAFsRef.current.forEach(id => {
        cancelAnimationFrame(id);
      });
      activeRAFsRef.current = [];
      
      // Clear memorial service cache if memory usage is high
      if (performance && performance.memory && performance.memory.usedJSHeapSize > 50000000) {
        console.log('Clearing memorial cache due to high memory usage');
        memorialService.clearCache();
      }
    };
    
    // Run cleanup on unmount
    return () => {
      if (window.memorialWallCleanup) {
        window.memorialWallCleanup();
        delete window.memorialWallCleanup;
      }
    };
  }, []);

  // Safari detection - applied at initialization phase
  useEffect(() => {
    const safariDetected = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const iOSDetected = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    setIsSafari(safariDetected);
    setIsIOS(iOSDetected);
    
    // Safari optimization - preload the image
    if (safariDetected || iOSDetected) {
      // Preload wall images for Safari
      const img = new Image();
      img.src = isMobile ? wallImageMobile : wallImage;
      
      // Add Safari-specific optimization classes
      document.body.classList.add('safari-browser');
      
      if (iOSDetected) {
        document.body.classList.add('ios-safari');
      }
      
      try {
        if (window !== window.parent) {
          document.body.classList.add('safari-iframe');
        }
      } catch (e) {
        document.body.classList.add('safari-iframe');
      }
      
      // Add a special class to background container for Safari optimizations
      const bgContainer = document.getElementById('background-container');
      if (bgContainer) {
        bgContainer.classList.add('safari-bg-container');
      }
    }
  }, [isMobile, wallImage, wallImageMobile]);
  
  // Very basic static background styles - position will be updated directly in the DOM
  const backgroundStyle = useMemo(() => {
    // Common styles for all browsers
    const commonStyles = {
      backgroundImage: `url(${isMobile ? wallImageMobile : wallImage})`,
      backgroundSize: '200%',
      backgroundPosition: 'center 0%', // Default starting position
      transformStyle: 'preserve-3d', // Better 3D handling
      backfaceVisibility: 'hidden', // Prevents flickering
    };
    
    // Safari-specific styles
    if (isSafari || isIOS) {
      return {
        ...commonStyles,
        // Safari-specific hardware acceleration hints
        WebkitBackfaceVisibility: 'hidden',
        WebkitTransformStyle: 'preserve-3d',
        WebkitPerspective: '1000px',
        // Safari benefits from explicit will-change
        willChange: 'background-position',
      };
    }
    
    // Other browsers
    return {
      ...commonStyles,
      // Chrome/Firefox benefit from simple hardware acceleration
      transform: 'translateZ(0)',
      willChange: 'background-position',
    };
  }, [isMobile, wallImage, wallImageMobile, isSafari, isIOS]);
  
  return (
    <div 
      className="memorial-wall-container" 
      ref={containerRef}
      aria-live="polite"
    >
      {/* Skip to content link for accessibility */}
      <a href="#section-1-content" className="skip-link">
        Skip to memorial content
      </a>
      
      {/* Hidden element for screen reader announcements */}
      <div id="memorial-announcement" className="sr-only" aria-live="polite"></div>
      
      {/* Background container with the wall image */}
      <div
        className="background-container"
        aria-hidden="true"
        id="background-container"
      >
        {/* Wall image with minimal React interaction for smoother scrolling */}
        <div
          className="wall-background"
          id="wall-background"
          style={backgroundStyle}
          data-parallax="true"
        ></div>
      </div>

      {/* Ambient atmosphere layers - creates sense of place */}
      <div className="ambient-vignette" aria-hidden="true"></div>
      <div className="ambient-light-beam" aria-hidden="true"></div>

      {/* Parallax depth layers - different scroll rates for depth */}
      <div className="parallax-layer parallax-layer-deep" aria-hidden="true" data-parallax-rate="0.3">
        {/* Deep layer - slow floating elements */}
        <div className="floating-leaf"></div>
        <div className="floating-leaf"></div>
        <div className="floating-leaf"></div>
      </div>
      <div className="parallax-layer parallax-layer-mid" aria-hidden="true" data-parallax-rate="0.5">
        {/* Mid layer - light shafts */}
        <div className="light-shaft"></div>
        <div className="light-shaft"></div>
      </div>
      <div className="parallax-layer parallax-layer-near" aria-hidden="true" data-parallax-rate="0.7">
        {/* Near layer - dust particles (moves faster with scroll) */}
      </div>

      <div className="ambient-particles" aria-hidden="true">
        <div className="dust-mote"></div>
        <div className="dust-mote"></div>
        <div className="dust-mote"></div>
        <div className="dust-mote"></div>
        <div className="dust-mote"></div>
        <div className="dust-mote"></div>
        <div className="dust-mote"></div>
        <div className="dust-mote"></div>
      </div>

      {/* Intro Overlay */}
      {showIntro && (
        <div className="intro-overlay" onClick={handleIntroClose}>
          <div className="intro-content" onClick={(e) => e.stopPropagation()}>
            <h2>October 7th Memorial Wall</h2>
            <p>
              This interactive memorial wall honors the victims of the October 7th, 2023 attack. 
              Scroll down to explore the wall and click on any note to learn about their story.
            </p>
            <button className="intro-close-button" onClick={handleIntroClose}>
              Begin Journey
            </button>
          </div>
        </div>
      )}
      
      {/* Fixed section headers that stay at the top */}
      <div className="wall-sections-container">
        <div className="wall-sections-tabs">
          {sections.map((section) => (
            <div
              key={section.id}
              id={section.id}
              className={`wall-section ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => scrollToSection(section.id)}
            >
              <h2>{section.title}</h2>
            </div>
          ))}
        </div>

        {/* Search button - inside container for proper vertical alignment */}
        <div className="search-button-container">
          <button
            className={`search-toggle-button ${showSearch ? 'active' : ''}`}
            onClick={toggleSearch}
            aria-label={showSearch ? "Close search" : "Search memorials"}
            aria-expanded={showSearch}
          >
            {showSearch ? (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="2"/>
                <path d="M15.5 15.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Fade gradient overlay - separate element so it stays fixed on mobile */}
      <div className="nav-fade-overlay" aria-hidden="true"></div>
      
      {/* Search panel */}
      <div className={`search-panel ${showSearch ? 'visible' : ''}`}>
        <div className="search-input-container">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search memorials..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            aria-label="Search memorials"
          />
          {searchTerm && (
            <button 
              className="clear-search" 
              onClick={() => setSearchTerm('')}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="search-results">
            <p className="results-count">
              {filteredMemorials.length} {filteredMemorials.length === 1 ? 'result' : 'results'} found
            </p>
          </div>
        )}
      </div>
      
      {/* Scrollable content container */}
      <div className="scrollable-content" ref={scrollableContentRef}>
        <div 
          className="memorial-wall" 
          ref={wallRef}
          aria-label="Memorial wall containing notes about victims of October 7th"
          role="region"
        >
          {/* Section content areas */}
          {searchTerm ? (
            // Show all filtered results in a single view when searching
            <div id="search-results-content" className="section-content search-results-section">
              <h3 className="search-results-heading">
                Search Results for "{searchTerm}"
              </h3>
              <div className="search-results-grid">
              {filteredMemorials.length === 0 ? (
                <div className="no-results-message">
                  <p>No memorials found matching your search.</p>
                  <button 
                    className="clear-search-button" 
                    onClick={() => setSearchTerm('')}
                  >
                    Clear Search
                  </button>
                </div>
              ) : (
                filteredMemorials.map((note) => (
                  <div key={note.id} className="search-result-note-wrapper">
                    <Note
                      key={note.id}
                      name={note.name}
                      position={{ x: 50, y: 50 }} /* Center position since we're using a wrapper */
                      onClick={() => handleNoteClick(note)}
                      isMobile={isMobile}
                      viewed={viewedNotes[note.id]}
                      hasCandle={!!litCandles[note.id]}
                      hasStone={!!placedStones[note.id]}
                    />
                  </div>
                ))
              )}
              </div>
            </div>
          ) : (
            // Regular section view when not searching - dynamically rendered from config
            <>
              {sections.map((section) => (
                <div
                  key={section.id}
                  id={`${section.id}-content`}
                  className="section-content"
                >
                  {/* Section notes with virtualization */}
                  {visibleSections[section.id] && sectionMemorials[section.id]?.map((note) => (
                    <Note
                      key={note.id}
                      name={note.name}
                      position={note.position}
                      onClick={() => handleNoteClick(note)}
                      isMobile={isMobile}
                      viewed={viewedNotes[note.id]}
                      hasCandle={!!litCandles[note.id]}
                      hasStone={!!placedStones[note.id]}
                    />
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      
      {selectedNote && (
        <MemorialModal
          memorial={selectedNote}
          onClose={handleCloseInfo}
          onCandleLit={handleCandleLit}
          onStonePlaced={handleStonePlaced}
        />
      )}
      
      {/* Remember Someone button - random memorial discovery */}
      <RememberButton
        memorials={memorials}
        viewedNotes={viewedNotes}
        onSelect={handleNoteClick}
        excludeViewed={false}
      />

      {/* Help button */}
      <button
        className="help-button"
        onClick={() => setShowIntro(true)}
        aria-label="Show help information"
      >
        ?
      </button>
    </div>
  );
};

export default MemorialWall;